import { useQuery } from '@tanstack/react-query';

import type { LedgerLine, LedgerResponse } from '@dcc-erp/shared';

import { api } from '../api';

export interface LedgerFilters {
  accountCode?: string;
  // 'YYYYMM'
  period?: string;
  // ISO YYYY-MM-DD
  dateFrom?: string;
  dateTo?: string;
  // Line-level dimension filters (Phase 42-A4). UUIDs.
  branchId?: string;
  departmentId?: string;
}

// The shared LedgerLineSchema doesn't model the dimension ids (it's owned by
// another agent), but the API emits them on each row. Widen the wire shape here
// so the route can display branch/department without touching shared.
export type LedgerLineWithDims = LedgerLine & {
  branchId?: string | null;
  departmentId?: string | null;
};
export type LedgerResponseWithDims = Omit<LedgerResponse, 'rows'> & {
  rows: LedgerLineWithDims[];
};

// Returns null-shaped data until accountCode is set — the UI guards on
// `enabled` so the query doesn't fire on a partial filter.
export function useLedger(companyId: string | undefined, filters: LedgerFilters) {
  const qs = new URLSearchParams();
  if (filters.accountCode) qs.set('accountCode', filters.accountCode);
  if (filters.period) qs.set('period', filters.period);
  if (filters.dateFrom) qs.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) qs.set('dateTo', filters.dateTo);
  if (filters.branchId) qs.set('branchId', filters.branchId);
  if (filters.departmentId) qs.set('departmentId', filters.departmentId);
  const search = qs.toString();
  return useQuery({
    queryKey: ['ledger', companyId, filters],
    queryFn: () =>
      api<LedgerResponseWithDims>(`/companies/${companyId}/ledger${search ? `?${search}` : ''}`),
    enabled: !!companyId && !!filters.accountCode,
  });
}
